import DraggableModal from 'components/organisms/draggableModal';
import { useEffect, useReducer, type FC } from 'react';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import { convertSecondsToTime } from 'utils/timeHelper';
import { hoursToSeconds, minutesToSeconds } from 'date-fns';
import BaseSelectBox from 'components/atoms/base-select-box';
import { useFormik } from 'formik';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { useUpdateActionTimeManagement } from 'infrastructure/hooks/action-time-management/use-update-data';
import i18n from 'infrastructure/i18n';
import { ActionTypes } from 'infrastructure/enums/action-types';

import s from './styles.module.scss';

import type { TimePickerProps } from 'antd';
import type { ActionTimeManagementItem } from 'infrastructure/hooks/action-time-management/types';

const schema = yup.object().shape({
  reason: yup.string().required('Reason is required field'),
  time: yup.number().min(0).required('Time is required field'),
});

const reasons = [
  i18n.t('actionTimesManagement.reason1'),
  i18n.t('actionTimesManagement.reason2'),
  i18n.t('actionTimesManagement.reason3'),
];

interface ActionCellProps {
  record: ActionTimeManagementItem;
  disabled?: boolean;
}

const ActionCell: FC<ActionCellProps> = ({ record, disabled }) => {
  const [open, toggleOpen] = useReducer((v) => !v, false);
  const { t } = useTranslation();
  const { isLoading, updateActionTime } = useUpdateActionTimeManagement();

  const formik = useFormik({
    initialValues: schema.cast({}),
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: (values) => {
      if (typeof values.time === 'undefined' || !values.reason) return;

      updateActionTime(
        {
          actionGuid: record.actionGuid,
          actionType: record.actionType,
          patientGuid: record.patientGuid,
          module: record.source,
          newTime: values.time,
          reason: values.reason,
        },
        {
          onSuccess: () => {
            toggleOpen();
            formik.resetForm();
          },
        },
      );
    },
    validationSchema: schema,
  });

  const currentTime = convertSecondsToTime(record.time);
  const newTime = convertSecondsToTime(formik.values.time);
  const diff1 = (formik.values.time ?? 0) - record.time;
  const diff2 = record.time - (formik.values.time ?? 0);
  const isNegativeDiff = diff1 < 0;
  const diffTime1 = convertSecondsToTime(diff1);
  const diffTime2 = convertSecondsToTime(diff2);
  const defaultValue = formik.values.time
    ? dayjs(newTime, 'HH:mm:ss')
    : undefined;

  const onChange: TimePickerProps['onChange'] = (value) => {
    if (!value) {
      formik.setFieldValue('time', undefined);
      return;
    }

    const hh = value.get('hours');
    const mm = value.get('minutes');
    const ss = value.get('seconds');
    const hhInSec = hoursToSeconds(hh);
    const mmInSec = minutesToSeconds(mm);

    const newTimeInSec = hhInSec + mmInSec + ss;
    formik.setFieldValue('time', newTimeInSec);
  };

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  let title = t(`actionTypes.${record.actionType}`);
  if (
    record.actionType === ActionTypes.PatientComment ||
    record.actionType === ActionTypes.CCMPatientCallNotes
  ) {
    title = t(`actionTypes.general-comments`);
  }
  if (record.actionType === ActionTypes.CCMPatientNotes) {
    title = t(`actionTypes.clinical-notes`);
  }

  return (
    <>
      <BaseButton
        type="secondary"
        label={t('controls.edit')}
        width="100%"
        onClick={toggleOpen}
        disabled={disabled}
      />
      <DraggableModal
        isOpen={open}
        toggle={toggleOpen}
        title={title}
        subTitle={record.patientName}
        onSave={() => formik.handleSubmit()}
        modalClassName={s.modal}
        isSaveLoading={isLoading}
      >
        <div className={s.wrapper}>
          <div>
            <div className={s['time-wrapper']}>
              <p className={s.time}>
                {t('actionTimesManagement.currentTime')}:{' '}
                <span>{currentTime}</span>
              </p>
              <p className={s.time}>
                {t('actionTimesManagement.newTime')}: <span>{newTime}</span>
              </p>
              <p className={s.time}>
                {t('actionTimesManagement.difference')}:{' '}
                {formik.values.time !== undefined ? (
                  <span>
                    {isNegativeDiff ? '-' : '+'}
                    {isNegativeDiff ? diffTime2 : diffTime1}
                  </span>
                ) : (
                  <span>00:00:00</span>
                )}
              </p>
            </div>
          </div>
          <div>
            <label htmlFor="time-picker-new-value" className={s.label}>
              {t('actionTimesManagement.newTime')} <span>*</span>
            </label>
            <TimePicker
              id="time-picker-new-value"
              defaultValue={defaultValue}
              showNow={false}
              onChange={onChange}
              needConfirm
              style={{ width: '100%' }}
              status={formik.errors.time ? 'error' : undefined}
            />
            <p className={s['time-error']}>{formik.errors.time}</p>
          </div>
          <div>
            <label htmlFor="reason-select-box" className={s.label}>
              {t('actionTimesManagement.reason')} <span>*</span>
            </label>
            <BaseSelectBox
              id="reason-select-box"
              options={reasons.map((v) => ({ label: v, value: v }))}
              onChange={(v) => {
                formik.setFieldValue('reason', v);
              }}
              value={formik.values.reason}
              invalid={!!formik.errors.reason}
            />
            <p className={s.error}>{formik.errors.reason}</p>
          </div>
        </div>
      </DraggableModal>
    </>
  );
};
export default ActionCell;
